import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { usePreferencesStore } from '../store/preferencesStore';
import { useAuthStore } from '../store/authStore';
import { useOnboardingStore } from '../onboarding/onboardingState';
import { isSyntheticRun } from '../utils/syntheticRun';
import '../onboarding/onboarding.css';
import './UsageConsentCard.css';

/**
 * "Help us improve Apiboo?" — asked ONCE, right after sign-in (owner,
 * 16.10.2026). "Send usage data" is off until the user answers "Share"; the
 * features a signed-in user used are linked to their account in admin, so it
 * is a real question, not a default.
 *
 * Rules the owner set, and what keeps them:
 *   - Two EQUAL buttons. Same class, same size, neither one focused or
 *     coloured: no dark pattern.
 *   - Short and plain: only feature names, never requests, responses, data or
 *     files.
 *   - Once: either answer sets `usageDataAsked`; closing the app without
 *     answering asks again next time (nothing was chosen).
 *   - After the onboarding (terms first), never on top of it.
 *   - Not in a recorded demo or e2e run, unless the storyboard sets
 *     `window.__demoUsageConsent = true` to film it.
 */
export function shouldAskUsageConsent({ hydrated, usageDataAsked, isAuthenticated, onboardingOpen }) {
  if (!hydrated || usageDataAsked || !isAuthenticated || onboardingOpen) return false;
  if (isSyntheticRun()) {
    try {
      return typeof window !== 'undefined' && window.__demoUsageConsent === true;
    } catch {
      return false;
    }
  }
  return true;
}

export default function UsageConsentCard() {
  const { t } = useTranslation();
  const hydrated = usePreferencesStore((s) => s.hydrated);
  const usageDataAsked = usePreferencesStore((s) => s.usageDataAsked);
  const answer = usePreferencesStore((s) => s.answerUsageConsent);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const onboardingOpen = useOnboardingStore((s) => s.open);
  const cardRef = useRef(null);

  const open = shouldAskUsageConsent({ hydrated, usageDataAsked, isAuthenticated, onboardingOpen });

  // Focus the card itself, not a button: focusing either answer would nudge.
  useEffect(() => {
    if (open) cardRef.current?.focus();
  }, [open]);

  if (!open) return null;
  return createPortal(
    <div className="ob-overlay ucc-overlay">
      <div className="modal-backdrop" aria-hidden="true" />
      <div
        ref={cardRef}
        tabIndex={-1}
        className="modal-content ob-vars ucc-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ucc-title"
        aria-describedby="ucc-text"
        data-testid="usage-consent"
      >
        <h2 id="ucc-title" className="ucc-title">{t('usageConsent.title')}</h2>
        <p id="ucc-text" className="ucc-text">{t('usageConsent.text')}</p>
        <p className="ucc-note">{t('usageConsent.note')}</p>
        <div className="ucc-actions">
          <button type="button" className="ob-btn ucc-btn" onClick={() => answer(false)} data-testid="usage-consent-decline">
            {t('usageConsent.decline')}
          </button>
          <button type="button" className="ob-btn ucc-btn" onClick={() => answer(true)} data-testid="usage-consent-share">
            {t('usageConsent.share')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
